import { isWithinInterval } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Platform } from 'react-native';

import { appVersion } from 'utils/app-version-info';

import { ScreenOrientation } from '../types';

export { downloadFileFromUrl } from './download-file-from-url';
export { handleShare } from './handle-share';
export { saveFile } from './save-file';

export const appMessages = {
  // If no jwt is provided, the message will be 'APP::USER::' and it's up to the caller to handle that as a no user logged in.
  user: (jwt: string) => `APP::USER::${jwt || ''}`,
  // Generate a message to send to the webview to indicate that the app is loading.
  loading: (isLoading: boolean) => `APP::LOADING::${isLoading}`,
  device: (deviceId: string) => `APP::DEVICE::${deviceId}`,
  sensorData: (sensorData: string) => `APP::SENSOR_DATA::${sensorData}`,
  sensorsAvailability: (sensorsAvailable: boolean) => `APP::SENSORS_AVAILABLE::${sensorsAvailable}`,
  platform: `APP::PLATFORM::${Platform.OS}::VERSION::${appVersion}`,
  screenOrientation: (orientation: ScreenOrientation) => `APP::SCREEN_ORIENTATION::${orientation}`,
  localStorage: (localStorage: string) => `APP::LOCAL_STORAGE::${localStorage}`,
};

/**
 * Determines if the user's current time falls within a specified date range in a target timezone.
 *
 * This function takes startDateString and endDateString in 'YYYY-MM-DD' format from Sanity, representing dates in the
 * target timezone ie:(America/Argentina/Buenos_Aires).
 * The function then compares the user's current time, adjusted to the target timezone, against this date range.
 *
 * @param {string} startDateString - The start date of the range in 'YYYY-MM-DD' format (e.g., '2023-11-30').
 * @param {string} endDateString - The end date of the range in 'YYYY-MM-DD' format (e.g., '2023-11-30').
 * @return {boolean} - Returns true if the user's current time (in their local timezone) falls within
 *                     the specified date range in the target timezone. Returns false otherwise.
 */
export const isCampaignActive = ({
  startDateString,
  endDateString,
  timeZone,
}: {
  startDateString: string;
  endDateString: string;
  timeZone: string;
}): boolean => {
  // We are not keeping exact schedules at Sanity currently. Adding the schedules allows us to take the entire day into consideration.
  const startDate = new Date(`${startDateString}T00:00:00`);
  const endDate = new Date(`${endDateString}T23:59:59`);
  const userCurrentDate = new Date();

  // Convert user's current UTC time to target timezone
  const userCurrentInCampaignTimeZone = utcToZonedTime(userCurrentDate, timeZone);

  const isInRange = isWithinInterval(userCurrentInCampaignTimeZone, {
    start: startDate,
    end: endDate,
  });

  return isInRange;
};

export const extractNameAndFileType = (
  url: string
): {
  fileName: string;
  fileType: string;
  fileNameWithExtension: string;
} => {
  // Split the URL to get the last part, which is the file name with extension
  const parts = url.split('/');
  const fileNameWithExtension = parts[parts.length - 1];
  // Split the file name to separate the name and the file extension
  const fileParts = fileNameWithExtension.split('.');
  const fileName = fileParts.slice(0, -1).join('.');
  const fileType = fileParts[fileParts.length - 1];
  return { fileName, fileType, fileNameWithExtension };
};

export const isBase64 = (base64: string) => base64.startsWith('data:');

export const getBase64Type = (base64: string): string | undefined => {
  const regex = /^([^;]+):([^;]+)/;
  const match = base64.match(regex);

  if (!match) {
    return undefined;
  }
  switch (match[2]) {
    case 'image/jpg':
      return 'jpg';
    case 'svg':
    case 'image/svg+xml':
      return 'svg';
    case '@file/msword':
      return 'doc';
    case '@file/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'docx';
    case '@file/plain':
      return 'txt';
    case 'video/mp4':
      return 'mp4';
    case 'application/pdf':
      return 'pdf';
    case 'image/png':
      return 'png';

    default:
      return undefined;
  }
};

export const getFileExtension = (data: string): string | undefined => {
  if (isBase64(data)) {
    return getBase64Type(data);
  }
  return data.slice(data.lastIndexOf('.') + 1).split('?')[0];
};
